"use client";

import { Coffee, Sandwich, ShoppingBasket, Package, Send, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Businessservices() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    icon: Coffee,
    title: "The Coffee Bar",
    description: "Artisan espresso, ceremonial grade matcha, and seasonal lattes crafted with care.",
    features: ["Local Roasts", "House-made Syrups", "Oat & Almond Milk"]
  }, {
    icon: Sandwich,
    title: "Deli & Bakery",
    description: "Focaccia sandwiches and European pastries, sourced fresh from trusted local bakeries.",
    features: ["Fresh Focaccia", "European Pastries", "Breakfast Classics"]
  }, {
    icon: ShoppingBasket,
    title: "European Market",
    description: "A curated selection of imported grocery items, chocolates, and pantry staples from Europe.",
    features: ["Imported Goods", "Specialty Pantry", "Unique Gifts"]
  }, {
    icon: Package,
    title: "Package Shipping",
    description: "Reliable international and domestic shipping services to keep you connected with loved ones.",
    features: ["International Shipping", "Secure Handling", "Tracking Provided"]
  }, {
    icon: Send,
    title: "US Money Express",
    description: "Fast and secure money transfer services to send support wherever it's needed most.",
    features: ["Secure Transfers", "Quick Processing", "Global Reach"]
  }];
  return <section data-section-id="818" id="businessservices" className="relative bg-background text-foreground py-20 md:py-32 overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
      backgroundImage: "radial-gradient(circle, var(--primary) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 lg:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-6">
              03 / Our Offerings
            </div>
            <h2 className="font-default font-semibold tracking-tight text-foreground text-[2.5rem] sm:text-5xl lg:text-[4.5rem] leading-[0.95]">
              Everything You Need.
              <br />
              <span className="italic font-serif font-normal text-secondary">In Enfield.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <p className="font-default text-base lg:text-lg text-muted-foreground leading-relaxed mb-6">
              From artisan coffee and European delicacies to reliable package shipping 
              and US Money Express services — M&M Café & Market is your complete 
              neighborhood hub in Connecticut.
            </p>
            <Link to="/MenuPage" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-primary border-b border-primary pb-1 hover:gap-3 transition-all">
              Explore the menu
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <div data-index={i} key={i} className={`group border border-border bg-card flex flex-col p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg rounded-lg ${i === 1 ? "md:translate-y-12" : ""}`}>
                <div className="w-12 h-12 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center mb-6 group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300">
                  <Icon className="w-6 h-6" />
                </div>

                <h3 className="font-default text-2xl md:text-[28px] font-semibold tracking-tight text-card-foreground leading-tight mb-3">
                  {service.title}
                </h3>
                <p className="font-default text-sm text-muted-foreground leading-relaxed mb-8">
                  {service.description}
                </p>

                <ul className="mt-auto flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-2 pt-6 border-t border-border">
                  {service.features.map((feature, fi) => <li data-index={fi} key={fi} className="flex items-center gap-2 font-mono text-[11px] tracking-wide uppercase text-muted-foreground">
                      <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-md bg-secondary" />
                      {feature}
                    </li>)}
                </ul>
              </div>;
        })}
        </div>

        <div className="mt-20 md:mt-28 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-sm text-muted-foreground max-w-md">
            Looking for something specific from the market or planning a large lunch order? 
            We're here to help you find exactly what you need.
          </p>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-md font-mono text-xs tracking-[0.15em] uppercase shadow-md">
            <Link to="/VisitUsPage">
              Visit our shop
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}